import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { CaretDown } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { useDarkMode } from '../../../../hooks/useDarkMode';
import { spacesQueries } from '../../../../queries/spaces';
import { vectorQueries } from '../../../../queries/vector';
import { AuthService } from '../../../../service/AuthService';
import { useAuthStore } from '../../../../store/storeAuth';
import { useObjectStore } from '../../../../store/storeObjects';
import CreateFormModal from '../../../Forms/CreateFormModal';
import ObjectModal from '../../../Layout/LayoutComponents/FluxObjects/ObjectModal';
import KitModal from '../../../UI/KitModal';
import { VectorMemoryModal } from '../../../UI/VectorMemoryModal';
import { CreateItemType } from '../types';
import { AgentForm } from './components/AgentForm';
import { FluxbotForm } from './components/FluxbotForm';
import { VectorMemoryForm } from './components/VectorMemoryForm';
import { useItemHandlers } from './handlers/useItemHandlers';
import { useCreateItemModal } from './hooks/useCreateItemModal';

interface CreateItemModalProps {
  show: boolean;
  onHide: () => void;
  type: CreateItemType;
  initialFolderId?: string;
  initialSpaceId?: string;
}

const getTitle = (
  type: CreateItemType,
  expandCreateWithAi?: boolean,
): string => {
  switch (type) {
    case 'agent':
      return expandCreateWithAi ? 'Create Agent with Ai' : 'Create Agent';
    case 'interface':
      return 'Create New Interface';
    case 'fluxbot':
      return 'Create New Fluxbot';
    case 'object':
      return 'Create New Object';
    case 'vectorMemory':
      return 'Create New Vector Memory';
    default:
      return 'Create New Item';
  }
};

export const CreateItemModal: React.FC<CreateItemModalProps> = ({
  show,
  onHide,
  type,
  initialFolderId,
  initialSpaceId,
}) => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const queryClient = useQueryClient();
  const { setAuthUser } = useAuthStore(state => state);
  const {
    getObjectsBySpaceId,
    resetObjectUpdate,
    removeObject,
    setObjectUpdate,
  } = useObjectStore(state => state);

  const {
    name,
    setName,
    selectedSpaceId,
    setSelectedSpaceId,
    spaces,
    isCreating,
    setIsCreating,
    showVectorModal,
    setShowVectorModal,
    showObjectModal,
    setShowObjectModal,
    createdObjectSpaceId,
    vectorMemorySpaceId,
    setVectorMemorySpaceId,
    showInterfaceModal,
    setShowInterfaceModal,
    isCreateFormLoading,
    setIsCreateFormLoading,
    expandCreateWithAi,
    setExpandCreateWithAi,
    userPrompt,
    setUserPrompt,
    isCreatingFromShorthand,
    setIsCreatingFromShorthand,
    ensureFolderExists,
    loadSpaces,
    resetForm,
  } = useCreateItemModal(type, onHide, {
    initialFolderId,
    initialSpaceId,
  });

  const { handleCreate, handleCreateInterface } = useItemHandlers({
    type,
    name,
    selectedSpaceId,
    ensureFolderExists,
    loadSpaces,
    resetForm,
    onHide,
    setVectorMemorySpaceId,
    setShowVectorModal,
    setIsCreating,
    setIsCreateFormLoading,
    setShowInterfaceModal,
    expandCreateWithAi,
    userPrompt,
    setIsCreatingFromShorthand,
    initialFolderId,
  });

  useEffect(() => {
    if (show) {
      setShowVectorModal(false);
      setVectorMemorySpaceId(undefined);

      if (type === 'object') {
        const newObject = {
          id: Math.random().toString(36).substr(2, 9),
          name: '',
          text: [{ id: Math.random().toString(36).substr(2, 9), text: '' }],
          isCreating: true,
          localObject: false,
          sessionEnabled: false,
        };
        setObjectUpdate(newObject);
        setShowObjectModal(true);
      }

      if (type === 'fluxbot') {
        setSelectedSpaceId('private');
      }
    }
  }, [
    show,
    type,
    setShowVectorModal,
    setVectorMemorySpaceId,
    setShowObjectModal,
    setSelectedSpaceId,
  ]);

  useEffect(() => {
    if (show && type === 'interface') {
      setShowInterfaceModal(true);
    } else if (!show) {
      setShowInterfaceModal(false);
    }
  }, [show, type, setShowInterfaceModal]);

  if (type === 'interface' && showInterfaceModal) {
    return (
      <CreateFormModal
        show={showInterfaceModal}
        onHide={() => {
          setShowInterfaceModal(false);
          onHide();
        }}
        isCreateFormLoading={isCreateFormLoading}
        onCreateForm={handleCreateInterface}
        selectedSpaceId={selectedSpaceId === 'private' ? '' : selectedSpaceId}
        onSpaceChange={spaceId => setSelectedSpaceId(spaceId || 'private')}
        showSpaceSelector={true}
      />
    );
  }

  if (type === 'object' && showObjectModal) {
    return (
      <ObjectModal
        show={showObjectModal}
        onHide={async () => {
          setShowObjectModal(false);
          const currentObjects = getObjectsBySpaceId(
            createdObjectSpaceId || 'user',
          );
          const creatingObject = currentObjects.find(obj => obj.isCreating);
          if (creatingObject) {
            removeObject(creatingObject.id, createdObjectSpaceId || 'user');
          }
          resetObjectUpdate();
          await new Promise(resolve => setTimeout(resolve, 100));
          window.dispatchEvent(new CustomEvent('sidebar-refresh'));
          onHide();
        }}
        spaceId={createdObjectSpaceId}
      />
    );
  }

  if (type === 'vectorMemory' && showVectorModal) {
    return (
      <VectorMemoryModal
        spaceId={vectorMemorySpaceId}
        open={showVectorModal}
        onOpenChange={async state => {
          if (!state.open) {
            setShowVectorModal(false);
            setVectorMemorySpaceId(undefined);

            if (vectorMemorySpaceId) {
              await queryClient.invalidateQueries({
                queryKey: spacesQueries.all().queryKey,
              });
            } else {
              await queryClient.invalidateQueries({
                queryKey: vectorQueries.allNamespaces().queryKey,
              });
              const userRes = await AuthService.getMe();
              setAuthUser(userRes?.data);
            }

            window.dispatchEvent(new CustomEvent('sidebar-refresh'));
            onHide();
          }
        }}
      />
    );
  }

  const renderForm = () => {
    switch (type) {
      case 'agent':
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              gap: '1rem',
            }}
          >
            <AgentForm
              name={name}
              onNameChange={setName}
              selectedSpaceId={selectedSpaceId}
              onSpaceChange={setSelectedSpaceId}
              spaces={spaces}
              disabled={isCreating || isCreatingFromShorthand}
              showSpaceSelector={!initialFolderId}
            />
            <Accordion
              expanded={expandCreateWithAi}
              onChange={() => setExpandCreateWithAi(!expandCreateWithAi)}
              sx={{
                boxShadow: 'none',
                '&:before': { display: 'none' },
                width: '100%',
              }}
            >
              <AccordionSummary
                expandIcon={<CaretDown />}
                aria-controls='shorthand-content'
                id='shorthand-header'
                sx={{
                  padding: 0,
                  minHeight: '36px',
                  maxHeight: '36px',
                  height: '36px',
                  overflow: 'hidden',
                  '& .MuiAccordionSummary-content': {
                    margin: 0,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  },
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(106, 117, 255, 0.12)',
                    cursor: 'pointer',
                    '& .MuiTypography-root': { fontWeight: 'bold' },
                  },
                  pl: 1.5,
                  pr: 1.5,
                  '&.Mui-expanded': {
                    minHeight: '36px',
                    maxHeight: '36px',
                    height: '36px',
                  },
                }}
              >
                <Typography color='primary'>Create with Ai</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, pt: 2, mt: 1 }}>
                <TextField
                  name='userPrompt'
                  value={userPrompt}
                  onChange={e => setUserPrompt(e.target.value)}
                  fullWidth
                  multiline
                  rows={12}
                  label='Describe the agent you want to create'
                  placeholder="Describe what you want your agent to do. For example: 'Create an agent that takes a text input, generates an image based on it, and then responds with a summary.'"
                  sx={{
                    width: '100%',
                    '& .MuiInputBase-input': {
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-wrap',
                    },
                  }}
                />
              </AccordionDetails>
            </Accordion>
            <Box sx={{ alignSelf: 'flex-end' }}>
              <Button
                disabled={
                  !name.trim() ||
                  (expandCreateWithAi && !userPrompt.trim()) ||
                  isCreating ||
                  isCreatingFromShorthand ||
                  (!initialFolderId && !selectedSpaceId)
                }
                onClick={handleCreate}
                variant='contained'
                size='small'
                sx={{
                  backgroundColor: palette.blue[500],
                  '&:hover': {
                    backgroundColor: palette.blue[600],
                  },
                }}
              >
                {isCreatingFromShorthand ? (
                  <CircularProgress size={24} color='inherit' />
                ) : isCreating ? (
                  'Creating...'
                ) : (
                  'Create'
                )}
              </Button>
            </Box>
          </Box>
        );
      case 'fluxbot':
        return (
          <FluxbotForm
            name={name}
            onNameChange={setName}
            selectedSpaceId={selectedSpaceId}
            onSpaceChange={setSelectedSpaceId}
            spaces={spaces}
            disabled={isCreating}
          />
        );
      case 'vectorMemory':
        return (
          <VectorMemoryForm
            selectedSpaceId={selectedSpaceId}
            onSpaceChange={setSelectedSpaceId}
            spaces={spaces}
            disabled={isCreating}
          />
        );
      default:
        return null;
    }
  };

  const isFormValid = () => {
    if (type === 'agent' || type === 'fluxbot') {
      const nameOk = name.trim().length > 0;
      if (type === 'agent' && expandCreateWithAi) {
        return nameOk && userPrompt.trim().length > 0;
      }
      return nameOk;
    }
    return true;
  };

  const modalMaxWidth =
    type === 'agent' ? (expandCreateWithAi ? '700px' : '650px') : '520px';
  const isAgentType = type === 'agent';

  return (
    <KitModal
      show={
        show && type !== 'interface' && type !== 'object' && !showVectorModal
      }
      onHide={onHide}
      title={getTitle(type, expandCreateWithAi)}
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          maxWidth: modalMaxWidth,
        },
      }}
      footer={
        isAgentType ? undefined : (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant='outlined'
              onClick={onHide}
              disabled={isCreating || isCreatingFromShorthand}
              sx={{
                color: isDarkMode ? palette.gray[300] : palette.gray[700],
                borderColor: isDarkMode ? palette.gray[700] : palette.gray[300],
              }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              onClick={handleCreate}
              disabled={isCreating || isCreatingFromShorthand || !isFormValid()}
              sx={{
                backgroundColor: palette.blue[500],
                '&:hover': {
                  backgroundColor: palette.blue[600],
                },
              }}
            >
              {isCreatingFromShorthand ? (
                <CircularProgress size={24} color='inherit' />
              ) : isCreating ? (
                'Creating...'
              ) : (
                'Create'
              )}
            </Button>
          </Box>
        )
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: isAgentType ? 0 : 3,
          padding: 2,
        }}
      >
        {renderForm()}
      </Box>
    </KitModal>
  );
};
